import { memo, useCallback, useEffect, useMemo, useState } from "react";
import type { InspectionState } from "@/views/inspections/components/inspections-lists/InspectionsLists.d";
import { Icon } from "@/components/icons/Icon";
import { cn } from "@/lib/styles.utils";

export interface PaginationProps {
  lastPage: number;
  changePage: (page: number, status?: InspectionState | undefined) => void;
  initialPage?: number;
  newPagination?: boolean;
}

const Pagination = ({
  lastPage,
  initialPage = 1,
  changePage,
  newPagination,
}: PaginationProps) => {
  const [currentPage, setCurrenPage] = useState(
    initialPage === 0 ? 1 : initialPage,
  );

  useEffect(() => {
    if (newPagination) setCurrenPage(1);
  }, [newPagination]);

  const onPageClick = useCallback(
    (page: number) => {
      setCurrenPage(page);
      changePage(page - 1);
    },
    [setCurrenPage, changePage],
  );

  const shownPages = useMemo(() => {
    if (lastPage <= 6) {
      return Array.from({ length: lastPage }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    if (currentPage <= 2) {
      pages.push(1, 2, 3, "...", lastPage - 2, lastPage - 1, lastPage);
    } else if (currentPage === 3) {
      pages.push(1, 2, 3, 4, "...", lastPage - 1, lastPage);
    } else if (currentPage === lastPage) {
      pages.push(1, 2, 3, "...", lastPage - 2, lastPage - 1, lastPage);
    } else if (currentPage === lastPage - 1) {
      pages.push(
        1,
        2,
        "...",
        lastPage - 3,
        lastPage - 2,
        lastPage - 1,
        lastPage,
      );
    } else if (currentPage === lastPage - 2) {
      pages.push(
        1,
        "...",
        lastPage - 4,
        lastPage - 3,
        lastPage - 2,
        lastPage - 1,
        lastPage,
      );
    } else if (currentPage + 2 === lastPage - 1) {
      pages.push(
        currentPage - 2,
        currentPage - 1,
        currentPage,
        currentPage + 1,
        currentPage + 2,
        lastPage,
      );
    } else {
      pages.push(
        currentPage - 2,
        currentPage - 1,
        currentPage,
        currentPage + 1,
        currentPage + 2,
        "...",
        lastPage,
      );
    }

    return pages;
  }, [currentPage, lastPage]);

  return (
    <div
      data-testid="table-pagination"
      className={cn(
        "flex flex-row items-center gap-x-[5px] w-full md:w-fit",
        lastPage > 3 ? "max-md:justify-between" : "max-md:justify-center",
      )}
    >
      {lastPage > 3 ? (
        <button
          data-testid="first-button"
          className="w-8 h-8 bg-ib-white text-ib-black border border-ib-gray-borders rounded-[2px] disabled:bg-ib-gray-borders disabled:text-ib-gray-disabled"
          disabled={currentPage === 1}
          onClick={() => {
            if (currentPage !== 1) onPageClick(1);
          }}
        >
          <div className="w-full flex justify-center">
            <Icon name="doubleChevronLeft" className="size-5" />
          </div>
        </button>
      ) : null}
      <button
        data-testid="previous-button"
        className="w-8 h-8 bg-ib-white text-ib-black border border-ib-gray-borders rounded-[2px] disabled:bg-ib-gray-borders disabled:text-ib-gray-disabled"
        disabled={currentPage === 1}
        onClick={() => {
          if (currentPage !== 1) onPageClick(1);
        }}
      >
        <div className="w-full flex justify-center">
          <Icon name="chevronLeft" className="size-5" />
        </div>
      </button>
      {shownPages.map((page) => {
        if (page === "...")
          return (
            <div
              key={`between-pages`}
              data-testid="middle-points"
              className="w-8 h-8 bg-ib-white text-ib-black border border-ib-gray-borders rounded-[2px]"
            >
              <div className="w-full h-full flex  justify-center items-center">
                <Icon name="ellipsis" className="size-5" />
              </div>
            </div>
          );
        return (
          <button
            key={`page-${page}`}
            className="w-8 h-8 bg-ib-white text-ib-gray-darkest border border-ib-gray-borders rounded-[2px] text-[12px] leading-[20px] disabled:text-ib-white disabled:font-[IberiaTextBold] disabled:font-ib-bold disabled:bg-ib-red-main disabled:border-ib-red-main"
            disabled={page === currentPage}
            onClick={() => onPageClick(page as number)}
          >
            {page}
          </button>
        );
      })}
      <button
        data-testid="next-button"
        className="w-8 h-8 bg-ib-white text-ib-black border border-ib-gray-borders rounded-[2px] disabled:bg-ib-gray-borders disabled:text-ib-gray-disabled"
        disabled={lastPage === currentPage}
        onClick={() => {
          if (currentPage !== lastPage) onPageClick(currentPage + 1);
        }}
      >
        <div className="w-full flex justify-center">
          <Icon name="chevronRight" className="size-5" />
        </div>
      </button>
      {lastPage > 3 ? (
        <button
          data-testid="last-button"
          className="w-8 h-8 bg-ib-white text-ib-black border border-ib-gray-borders rounded-[2px] disabled:bg-ib-gray-borders disabled:text-ib-gray-disabled"
          disabled={lastPage === currentPage}
          onClick={() => {
            if (currentPage !== lastPage) onPageClick(lastPage);
          }}
        >
          <div className="w-full flex justify-center">
            <Icon name="doubleChevronRight" className="size-5" />
          </div>
        </button>
      ) : null}
    </div>
  );
};

export default memo(Pagination);
